/**
 * Battery-style visual for auto-calculated break durations.
 *
 * One battery = 1 hour, split into 4 quarter segments (15 min each). Breaks
 * longer than an hour chain multiple batteries; full hours render as a solid
 * block (no dividers) while the trailing partial hour stays segmented so the
 * quarter count reads at a glance even at small sizes. Fill color tracks how
 * many quarters are filled (1–4), not the total duration, so the palette
 * never grows past four colors regardless of how long the break is.
 */
import type { CSSProperties } from 'react';
import { formatBreakMinutes } from '@/config/modeConfig';

const QUARTER_COLORS: Record<1 | 2 | 3 | 4, string> = {
  1: '#f0a63e', // 1/4 filled (15 min)
  2: '#f0d43e', // 2/4 filled (30 min)
  3: '#5cbf60', // 3/4 filled (45 min)
  4: '#237a3f', // full battery (60 min)
};
const EMPTY_SEGMENT = '#e2e5ea';
const STROKE = '#000';

interface BatterySizing {
  bodyW: number;
  bodyH: number;
  nubW: number;
  nubH: number;
  border: number;
  unitGap: number; // gap between chained battery units
  segGap: number; // gap between the 4 quarter segments inside one battery
  pad: number; // inset between the outer border and the segment stack
}

const SIZES: Record<'normal' | 'compact', BatterySizing> = {
  normal: { bodyW: 10, bodyH: 15, nubW: 5, nubH: 1.5, border: 1, unitGap: 2, segGap: 0.75, pad: 0.75 },
  compact: { bodyW: 9, bodyH: 13, nubW: 4, nubH: 1.5, border: 1, unitGap: 1.5, segGap: 0.5, pad: 0.5 },
};

function BatteryUnit({ filled, sizing }: { filled: 1 | 2 | 3 | 4; sizing: BatterySizing }) {
  const { bodyW, bodyH, nubW, nubH, border, segGap, pad } = sizing;
  const nubStyle: CSSProperties = {
    width: nubW,
    height: nubH,
    background: STROKE,
    borderRadius: '1px 1px 0 0',
  };
  if (filled === 4) {
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <span style={nubStyle} />
        <span
          style={{
            width: bodyW,
            height: bodyH,
            border: `${border}px solid ${STROKE}`,
            borderRadius: 2,
            background: QUARTER_COLORS[4],
            boxSizing: 'border-box',
          }}
        />
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
      <span style={nubStyle} />
      <span
        style={{
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: segGap,
          padding: pad,
          width: bodyW,
          height: bodyH,
          border: `${border}px solid ${STROKE}`,
          borderRadius: 2,
          boxSizing: 'border-box',
        }}
      >
        {([1, 2, 3, 4] as const).map((seg) => (
          <span
            key={seg}
            style={{
              flex: 1,
              minHeight: 0,
              borderRadius: 0.5,
              background: seg <= filled ? QUARTER_COLORS[filled] : EMPTY_SEGMENT,
            }}
          />
        ))}
      </span>
    </span>
  );
}

/** Invisible spacer matching one battery unit's footprint, so the icon area always reserves the same width. */
function BatteryPlaceholder({ sizing }: { sizing: BatterySizing }) {
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
      <span style={{ width: sizing.nubW, height: sizing.nubH }} />
      <span style={{ width: sizing.bodyW, height: sizing.bodyH }} />
    </span>
  );
}

// Reserve room for a 2h30 break (2 full batteries + 1 partial) so the duration
// text after the icon lands in the same spot no matter how short the break is.
const MIN_UNITS = 3;

/** Renders one battery per hour of `minutes`, chained left to right; the trailing partial hour (if any) stays segmented. */
function BreakBatteryIcon({ minutes, compact }: { minutes: number; compact?: boolean }) {
  const sizing = SIZES[compact ? 'compact' : 'normal'];
  const quarters = Math.max(1, Math.round(minutes / 15));
  const fullCount = Math.floor(quarters / 4);
  const remainder = quarters % 4;
  const units: Array<1 | 2 | 3 | 4> = Array(fullCount).fill(4);
  if (remainder > 0) units.push(remainder as 1 | 2 | 3);
  const placeholderCount = Math.max(0, MIN_UNITS - units.length);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: sizing.unitGap }} title={`${minutes} min break`}>
      {units.map((u, i) => (
        <BatteryUnit key={i} filled={u} sizing={sizing} />
      ))}
      {Array.from({ length: placeholderCount }, (_, i) => (
        <BatteryPlaceholder key={`ph-${i}`} sizing={sizing} />
      ))}
    </span>
  );
}

interface BreakDurationBadgeProps {
  minutes: number;
  isOverlap?: boolean;
  compact?: boolean;
}

/** Slim single-line strip: "BREAK:" label, battery icon(s), h/min duration — all inline so it reads as a thin divider, not a tall block. */
export function BreakDurationBadge({ minutes, isOverlap, compact }: BreakDurationBadgeProps) {
  const labelStyle: CSSProperties = {
    fontSize: compact ? 13 : 14,
    fontWeight: 800,
    letterSpacing: '0.02em',
    fontFamily: 'monospace',
    lineHeight: 1,
    color: '#000',
  };
  const valueStyle: CSSProperties = {
    fontSize: compact ? 13 : 14,
    fontWeight: 800,
    fontFamily: 'monospace',
    lineHeight: 1,
    color: '#000',
    // Fixed (monospace) width covers the longest duration string ("2h30min")
    // so the badge's total width — and thus its centered position — stays
    // constant no matter how short the actual break is.
    display: 'inline-block',
    width: '7ch',
    textAlign: 'left',
  };
  if (isOverlap) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: compact ? 3 : 4,
          padding: compact ? '1px 7px' : '1px 8px',
          borderRadius: 999,
          background: '#fde8e8',
          border: '1px solid #dc3545',
        }}
      >
        <span style={{ ...labelStyle, color: '#dc3545' }}>OVERLAP:</span>
        <span style={{ ...valueStyle, color: '#dc3545' }}>{formatBreakMinutes(Math.abs(minutes))}</span>
      </span>
    );
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 3 : 4,
        padding: compact ? '1px 7px' : '1px 8px',
        borderRadius: 999,
        background: '#f3f5f8',
        border: 'none',
      }}
    >
      <span style={labelStyle}>BREAK:</span>
      <BreakBatteryIcon minutes={minutes} compact={compact} />
      <span style={valueStyle}>{formatBreakMinutes(minutes)}</span>
    </span>
  );
}
